import * as THREE from 'three';
import { INK_MARGIN, type InkStroke, type TattooPreset } from '../../../content/characters/rigs/human-markings.js';
import type { ConnectedBody } from './connected-body.js';

const SIZE = 256, SPAN = 1 + 2 * INK_MARGIN;
/** A design point as a texture coordinate. */
export const inkUv = (x: number, y: number): [number, number] => [(x + INK_MARGIN) / SPAN, (y + INK_MARGIN) / SPAN];

const smooth = (edge: number, d: number) => THREE.MathUtils.clamp(.5 - d / edge, 0, 1);
const hash = (x: number, y: number, seed: number) => { const n = Math.sin(x * 127.1 + y * 311.7 + seed * 74.7) * 43758.5453; return n - Math.floor(n); };
/** Smooth value noise in [0, 1] on a `cell`-pixel lattice. */
function noise(x: number, y: number, cell: number, seed: number) {
  const gx = x / cell, gy = y / cell, x0 = Math.floor(gx), y0 = Math.floor(gy), fx = gx - x0, fy = gy - y0;
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
  const top = THREE.MathUtils.lerp(hash(x0, y0, seed), hash(x0 + 1, y0, seed), sx), bottom = THREE.MathUtils.lerp(hash(x0, y0 + 1, seed), hash(x0 + 1, y0 + 1, seed), sx);
  return THREE.MathUtils.lerp(top, bottom, sy);
}

/** Coverage of one stroke, in pixels: `paint` receives each covered pixel and its coverage. */
function cover(stroke: InkStroke, size: number, paint: (index: number, amount: number) => void) {
  const scale = size / SPAN, edge = 1.6, points = stroke.points.map(([x, y]) => [(x + INK_MARGIN) * scale, (y + INK_MARGIN) * scale]);
  const lengths = [0]; for (let i = 1; i < points.length; i++) lengths.push(lengths[i - 1] + Math.hypot(points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1]));
  const total = lengths[lengths.length - 1] || 1, [start, end] = stroke.ends ?? [1, 1];
  const half = (along: number) => stroke.width * scale / 2 * THREE.MathUtils.lerp(start, end, along / total);
  const reach = stroke.fill ? edge : stroke.width * scale / 2 * Math.max(start, end) + edge;
  const xs = points.map(p => p[0]), ys = points.map(p => p[1]);
  const x0 = Math.max(0, Math.floor(Math.min(...xs) - reach)), x1 = Math.min(size - 1, Math.ceil(Math.max(...xs) + reach));
  const y0 = Math.max(0, Math.floor(Math.min(...ys) - reach)), y1 = Math.min(size - 1, Math.ceil(Math.max(...ys) + reach));
  const segments = stroke.fill ? points.map((p, i) => [p, points[(i + 1) % points.length]]) : points.slice(1).map((p, i) => [points[i], p]);
  for (let py = y0; py <= y1; py++) for (let px = x0; px <= x1; px++) {
    const x = px + .5, y = py + .5;
    let best = Infinity, inside = false;
    segments.forEach(([a, b], i) => {
      const dx = b[0] - a[0], dy = b[1] - a[1], t = THREE.MathUtils.clamp(((x - a[0]) * dx + (y - a[1]) * dy) / (dx * dx + dy * dy || 1), 0, 1);
      const distance = Math.hypot(x - a[0] - dx * t, y - a[1] - dy * t);
      if (stroke.fill) {
        best = Math.min(best, distance);
        if ((a[1] > y) !== (b[1] > y) && x < a[0] + (y - a[1]) / (b[1] - a[1]) * dx) inside = !inside;
      } else best = Math.min(best, distance - half(lengths[i] + (lengths[i + 1] - lengths[i]) * t));
    });
    const amount = smooth(edge, stroke.fill ? (inside ? -best : best) : best);
    if (amount > 0) paint(py * size + px, amount);
  }
}

/**
 * A design as coverage in [0, 1], aged like old skin ink: soft edges, a faint bleed round each line and an uneven, mottled fill.
 * Row 0 is the bottom of the design.
 */
export function rasterizeTattoo(strokes: InkStroke[], size = SIZE, seed = 1): Float32Array {
  const ink = new Float32Array(size * size);
  for (const stroke of strokes) cover(stroke, size, stroke.cut ? (i, amount) => { ink[i] *= 1 - amount; } : (i, amount) => { ink[i] = Math.max(ink[i], amount); });
  // The bleed: ink spread a few pixels into the skin round every line.
  const blur = (source: Float32Array, radius: number, horizontal: boolean) => {
    const out = new Float32Array(source.length);
    for (let a = 0; a < size; a++) for (let b = 0; b < size; b++) {
      let sum = 0;
      for (let k = -radius; k <= radius; k++) { const c = THREE.MathUtils.clamp(b + k, 0, size - 1); sum += source[horizontal ? a * size + c : c * size + a]; }
      out[horizontal ? a * size + b : b * size + a] = sum / (radius * 2 + 1);
    }
    return out;
  };
  const radius = Math.max(1, Math.round(size / 64)), bleed = blur(blur(ink, radius, true), radius, false);
  const result = new Float32Array(size * size);
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const i = y * size + x, mottle = .78 + .22 * noise(x, y, size / 14, seed), grain = .9 + .1 * noise(x, y, 2, seed + 1);
    result[i] = Math.min(1, ink[i] * .9 + bleed[i] * .22 * (1 - ink[i])) * mottle * grain;
  }
  return result;
}

const textures = new Map<string, THREE.DataTexture>();
/** The design's texture: white, its coverage in alpha, to be tinted by the ink colour. Shared by every figure wearing the design. */
export function tattooTexture(preset: TattooPreset): THREE.DataTexture {
  let texture = textures.get(preset.id);
  if (texture) return texture;
  const coverage = rasterizeTattoo(preset.strokes), pixels = new Uint8Array(SIZE * SIZE * 4);
  for (let i = 0; i < coverage.length; i++) {
    const tone = 232 + 23 * hash(i % SIZE, Math.floor(i / SIZE), 9);
    pixels[i * 4] = pixels[i * 4 + 1] = pixels[i * 4 + 2] = tone; pixels[i * 4 + 3] = Math.round(coverage[i] * 255);
  }
  texture = new THREE.DataTexture(pixels, SIZE, SIZE, THREE.RGBAFormat);
  texture.colorSpace = THREE.SRGBColorSpace; texture.generateMipmaps = true; texture.minFilter = THREE.LinearMipmapLinearFilter; texture.magFilter = THREE.LinearFilter;
  texture.anisotropy = 4; texture.name = `tattoo:${preset.id}`; texture.needsUpdate = true;
  textures.set(preset.id, texture);
  return texture;
}

/** The ink material: the design tinted by its ink, laid over the skin without hiding it where the ink thins. */
export function tattooMaterial(preset: TattooPreset, ink: number) {
  const material = new THREE.MeshStandardMaterial({ color: ink, roughness: .9, metalness: 0, map: tattooTexture(preset), transparent: true, depthWrite: false,
    polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
  material.name = 'tattoo';
  return material;
}

/** Where a design lies round a limb or the trunk: `angle` faces its centre (π/2 is the front), `spread` is its width in radians. */
export interface InkWindow { bottom: number; height: number; angle: number; spread: number }
type Region = Parameters<ConnectedBody['garment']>[0][number];
const LIFT = .0009, TAU = Math.PI * 2;
/** Widest a window may wrap, so the clear seam opposite its centre always remains. */
const MOST = (TAU - .3) / SPAN;

/**
 * Ink on the body's own skin triangles, wrapped round `axis` (the limb's or trunk's centre at a height): the part of the connected skin under the window is cut out and lifted a hair above it, with
 * the skin's own bone weights, and the design is mapped onto it. The decal can never stretch away from the skin, since it is the skin.
 * `cuts` keep other skin out (the crotch, for a thigh). A window much taller than it is wide repeats the design down it, and one much wider is narrowed, so the design keeps its proportions.
 */
export function skinInk(body: ConnectedBody, regions: Region[], window: InkWindow, axis: (y: number) => [number, number, number, number], side = 0, cuts: ((point: THREE.Vector3) => number)[] = []): THREE.BufferGeometry[] {
  // Skin far outside the lofted ring is where the piece joins another (a shoulder into the chest), and would smear the design.
  const near = (point: THREE.Vector3) => { const [x, z, rx, rz] = axis(point.y); return 1.3 - Math.hypot((point.x - x) / rx, (point.z - z) / rz); };
  cuts = [...cuts, near];
  const margin = INK_MARGIN * window.height, around = body.garment(regions, window.bottom - margin, window.bottom + window.height + margin, 0, side, cuts);
  const position = around.getAttribute('position'), centre = axis;
  const offset = (point: THREE.Vector3) => { const [x, z] = centre(point.y); return Math.atan2(point.z - z, point.x - x); };
  const turn = (point: THREE.Vector3) => { const delta = offset(point) - window.angle; return delta - TAU * Math.round(delta / TAU); };
  // The skin's radius facing the window, to keep the design's proportions in metres.
  let radius = 0, counted = 0;
  for (let i = 0; i < position.count; i++) {
    const point = new THREE.Vector3().fromBufferAttribute(position, i), [x, z] = centre(point.y);
    if (Math.abs(turn(point)) < Math.min(window.spread, MOST) / 2) { radius += Math.hypot(point.x - x, point.z - z); counted++; }
  }
  around.dispose();
  if (!counted) return [];
  radius /= counted;
  let spread = Math.min(window.spread, MOST);
  if (spread * radius > window.height * 1.3) spread = window.height * 1.15 / radius;
  const tiles = Math.max(1, Math.round(window.height / (spread * radius)));
  const pieces: THREE.BufferGeometry[] = [];
  for (let tile = 0; tile < tiles; tile++) {
    const height = window.height / tiles, bottom = window.bottom + tile * height, edge = INK_MARGIN * height;
    const half = spread / 2 * SPAN, geometry = body.garment(regions, bottom - edge, bottom + height + edge, LIFT, side, [...cuts, point => half - Math.abs(turn(point))]);
    const points = geometry.getAttribute('position'), uvs = geometry.getAttribute('uv'), normals = geometry.getAttribute('normal');
    if (!points.count) { geometry.dispose(); continue; }
    const point = new THREE.Vector3(), normal = new THREE.Vector3();
    for (let i = 0; i < points.count; i++) {
      // Sample the skin point beneath the lifted vertex, so the mapping is the skin's own.
      point.fromBufferAttribute(points, i).addScaledVector(normal.fromBufferAttribute(normals, i), -LIFT);
      uvs.setXY(i, ...inkUv(.5 - turn(point) / spread, (point.y - bottom) / height));
    }
    pieces.push(geometry);
  }
  return pieces;
}

/** Leaves out skin lying flat (a shoulder's top, the floor of an armpit): wrapping round an upright axis would smear a design over it. */
function level(geometry: THREE.BufferGeometry) {
  const position = geometry.getAttribute('position'), keep: number[] = [], a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
  for (let t = 0; t < position.count; t += 3) {
    a.fromBufferAttribute(position, t); b.fromBufferAttribute(position, t + 1).sub(a); c.fromBufferAttribute(position, t + 2).sub(a);
    if (Math.abs(b.cross(c).normalize().y) < .85) keep.push(t, t + 1, t + 2);
  }
  if (keep.length === position.count) return geometry;
  const result = new THREE.BufferGeometry();
  for (const [name, attribute] of Object.entries(geometry.attributes)) {
    const values = new (attribute.array.constructor as Float32ArrayConstructor)(keep.length * attribute.itemSize);
    keep.forEach((from, to) => { for (let k = 0; k < attribute.itemSize; k++) values[to * attribute.itemSize + k] = attribute.getComponent(from, k); });
    result.setAttribute(name, new THREE.BufferAttribute(values, attribute.itemSize));
  }
  geometry.dispose();
  return result;
}
